import React, { useState } from 'react';
import {
  CustomButton,
  CustomInput,
  CustomSelect,
  CustomShipmentText,
  CustomTitle,
} from '../../../components';
import { Checkbox, Divider, Flex, Form, message, Row } from 'antd';
import PhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useSpin } from '../../../components/SpinContext';
import {
  externalShipmentAlert,
  ExternalShipmentAlertModel,
} from '../../../apis/shipment';
import { showCustomSuccessMessage } from '../../../common/utils';

type NotificationMethod = 'SMS' | 'EMAIL';

interface TimeRange {
  id: number;
  name: string;
}

interface ExternalMapAlertsFormProps {
  setShowAlertForm: (show: boolean) => void;
  externalToken: string;
}

const timeRangeList: TimeRange[] = [
  { id: 15, name: '15 mins' },
  { id: 30, name: '30 mins' },
  { id: 60, name: '60 mins' },
  { id: 120, name: '120 mins' },
];

/**
 * Form component for configuring external shipment alerts
 *
 * @param setShowAlertForm - Callback to show/hide the alert form
 * @param externalToken - Token for external authentication
 */
const ExternalMapAlertsForm: React.FC<ExternalMapAlertsFormProps> = ({
  setShowAlertForm,
  externalToken,
}) => {
  const [_messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();

  const { showSpin, hideSpin } = useSpin();
  const [_isFormValid, setIsFormValid] = useState(false);
  const [ETAAlerts, setETAAlerts] = useState(false);
  const [_allValues, setAllValues] = useState(false);

  const [notifyVia, setNotifyVia] = useState<NotificationMethod>('SMS');

  const handleMediumChange = (value: NotificationMethod): void => {
    setNotifyVia(value);
    form.setFieldsValue({ contact_info: '' }); // Reset contact info when medium changes
  };

  const onFinish = (): void => {
    const model = form.getFieldsValue() as ExternalShipmentAlertModel;

    const alertSelected =
      model.ETA_CHANGE ||
      model.DELIVERY_EXCEPTION ||
      model.SHIPMENT_DELIVERED ||
      model.SHIPMENT_OUT_FOR_DELIVERY;
    if (!alertSelected) {
      message.error('Get notified field is required');
      return;
    }

    model.token = externalToken;

    const alertTypes = [
      'ETA_CHANGE',
      'DELIVERY_EXCEPTION',
      'SHIPMENT_OUT_FOR_DELIVERY',
      'SHIPMENT_DELIVERED',
    ] as const;
    model.alert_types = alertTypes.filter(
      (key) => model[key as keyof typeof model]
    );

    alertTypes.forEach((key) => delete model[key as keyof typeof model]);

    if (!ETAAlerts) {
      delete model.eta_threshold;
    }

    submitAlertSettings(model);
  };

  const submitAlertSettings = (model: ExternalShipmentAlertModel): void => {
    setIsFormValid(true);
    showSpin();

    externalShipmentAlert(model)
      .then((res) => {
        const data = res?.data || null;
        if (data?.data?.is_success) {
          showCustomSuccessMessage(data?.data?.message || '');
          setShowAlertForm(false);
          form.resetFields();
          setETAAlerts(false);
        } else {
          message.error(data?.data?.message || 'Failed to save settings');
        }
      })
      .catch(() => {})
      .finally(() => {
        hideSpin();
        setIsFormValid(false);
      });
  };

  return (
    <div>
      <Flex
        gap={18}
        align='center'
        style={{ marginBottom: '20px', position: 'relative' }}
      >
        <svg
          style={{
            left: '15px',
            color: 'var(--text-primary)',
            cursor: 'pointer',
          }}
          onClick={() => setShowAlertForm(false)}
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M13.3333 7.33329H5.21996L8.94663 3.60663L7.99996 2.66663L2.66663 7.99996L7.99996 13.3333L8.93996 12.3933L5.21996 8.66663H13.3333V7.33329Z'
            fill='black'
          />
        </svg>

        <CustomTitle
          text={'Get updates'}
          fontSize={18}
          fontWeight={700}
          lineHeight='24px'
          marginTop={'0px'}
          color='var(--text-primary)'
          marginBottom='0'
        />
      </Flex>

      <Form
        form={form}
        layout='vertical'
        onFinish={onFinish}
        onValuesChange={(_changedValues, allValues) => {
          setAllValues(allValues);
        }}
        initialValues={{ notification_method: 'SMS', eta_threshold: 15 }}
      >
        <CustomShipmentText
          text={'Notify via...'}
          fontSize='14px'
          fontWeight={600}
          marginBottom='4px'
        />

        <Flex gap={8}>
          <Form.Item name={'notification_method'}>
            <CustomSelect
              placeholder='Select'
              width='110px'
              height='36px'
              border='0px'
              bgColor='var(--light-gray)'
              textColor='var(--text-secondary)'
              borderRadius='8px'
              placeholderColor='var(--text-secondary)'
              lineHeight='38px'
              onChange={handleMediumChange}
              options={[
                { value: 'SMS', label: 'SMS' },
                { value: 'EMAIL', label: 'Email' },
              ]}
            />
          </Form.Item>

          {notifyVia === 'SMS' ? (
            <Form.Item
              name='contact_info'
              style={{ width: '100%' }}
              rules={[
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.reject(
                        new Error('Phone number is required')
                      );
                    }
                    try {
                      const formatted = value.startsWith('+')
                        ? value
                        : `+${value}`;
                      if (isValidPhoneNumber(formatted)) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject(
                          new Error('Invalid phone number')
                        );
                      }
                    } catch {
                      return Promise.reject(new Error('Invalid phone number'));
                    }
                  },
                },
              ]}
            >
              <PhoneInput
                country={'us'}
                onChange={(_value, _countryData, _e, formattedValue) => {
                  form.setFieldsValue({ phone_number: formattedValue });
                }}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name='contact_info'
              style={{ width: '100%' }}
              rules={[
                { required: true, message: 'Email is required' },
                {
                  type: 'email',
                  message: 'Invalid email address',
                },
              ]}
            >
              <CustomInput
                placeholder='Enter email'
                height='36px'
                type='text'
                border='1px solid #E8E8E8'
                bgColor='var(--light-gray)'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                placeholderColor='var(--text-secondary)'
                lineHeight='38px'
              />
            </Form.Item>
          )}
        </Flex>
        <Divider />
        <CustomTitle
          text={'Get notified when...'}
          fontSize={14}
          fontWeight={600}
          lineHeight='16px'
          marginTop={'0px'}
          color='var(--text-primary)'
          marginBottom='4px'
        />
        <Form.Item>
          <Flex align='center' gap={4} wrap='nowrap'>
            <Form.Item name='ETA_CHANGE' valuePropName='checked' noStyle>
              <Checkbox
                className='small-checkbox external-map-alerts-eta-checkbox'
                onChange={(e) => setETAAlerts(e.target?.checked || false)}
                style={{ fontSize: '14px', fontWeight: '400' }}
              >
                ETA changes by more than
              </Checkbox>
            </Form.Item>

            <Form.Item name='eta_threshold' noStyle>
              <CustomSelect
                placeholder='Select'
                height='28px'
                border='0px'
                width={'100px'}
                disabled={!ETAAlerts}
                bgColor='var(--light-gray)'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                placeholderColor='var(--text-secondary)'
                lineHeight='28px'
                options={timeRangeList.map((opt) => ({
                  value: opt.id,
                  label: opt.name,
                }))}
              />
            </Form.Item>
          </Flex>
        </Form.Item>
        <Form.Item name='DELIVERY_EXCEPTION' valuePropName='checked'>
          <Checkbox
            className='small-checkbox'
            style={{ fontSize: '14px', fontWeight: '400' }}
          >
            A delivery exception has occurred
          </Checkbox>
        </Form.Item>
        <Form.Item name='SHIPMENT_OUT_FOR_DELIVERY' valuePropName='checked'>
          <Checkbox
            className='small-checkbox'
            style={{ fontSize: '14px', fontWeight: '400' }}
          >
            Shipment is out for delivery
          </Checkbox>
        </Form.Item>
        <Form.Item name='SHIPMENT_DELIVERED' valuePropName='checked'>
          <Checkbox
            className='small-checkbox'
            style={{ fontSize: '14px', fontWeight: '400' }}
          >
            Shipment delivered
          </Checkbox>
        </Form.Item>
        <div>
          <Row gutter={[0, 8]} style={{ marginTop: '42px' }}>
            <CustomButton
              text={'Save'}
              width={'100%'}
              height='36px'
              borderRadius='8px'
              bgColor={'#000'}
              color={'#fff'}
              border='0px'
              fontSize='14px'
              fontWeight={600}
              htmlType='submit'
            />
            <CustomButton
              key='button1'
              width={'100%'}
              onClick={() => setShowAlertForm(false)}
              text={'Cancel'}
              bgColor='transparent'
              color='black'
              fontSize='14px'
              fontWeight='600'
              boxShadow='none'
              height='36px'
            />
          </Row>
        </div>
      </Form>
      {contextHolder}
    </div>
  );
};

export default ExternalMapAlertsForm;
